import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card, CardBody, CardText } from 'reactstrap';
import useGetArticleBySlug from '../../api/useGetArticleBySlug';
import ApiError from '../../api/ApiError';
import HtmlComponent from '../shared/HtmlComponent';
import DisplayQueryError from '../shared/DisplayQueryError';
import LoadingSpinner from '../shared/LoadingSpinner';
import DocumentHead from '../shared/DocumentHead';
import NotFoundPage from './NotFoundPage';
import Header from '../shared/Header';
import FeedbackBtn from '../shared/FeedbackBtn';
import { ArticleMetricType } from '../../types';
import { onGiveArticleFeedback, recordArticleView } from '../../lib/feedback';
import useTrackPageView, {
    useTrackArticleView
} from '../../hooks/useTrackPageView.ts';
import usePagesUserId from '../../hooks/usePagesUserId.ts';
import useBotPagesConfig from '../../hooks/useBotPagesConfig.ts';
import { useMsal } from '@azure/msal-react';

interface ArticleProps {
    slug: string;
    from: string;
}

const Article: React.FC<ArticleProps> = ({ slug, from }) => {
    useTrackPageView();
    useTrackArticleView(slug, from);
    const { clientId, requiresAuthentication, structuredDataEnabled } = useBotPagesConfig();
    const msalInfo = useMsal();
    const pagesUserId = usePagesUserId();
    const tagsDropdownRef = useRef<HTMLDivElement>(null);
    const tagsSidebarRef = useRef<HTMLElement>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const getArticleBySlug = useGetArticleBySlug({ slug });

    const {
        data: article,
        isLoading,
        error,
        refetch
    } = useQuery(['articleBySlug', slug], getArticleBySlug);

    // Record the article view once the article (and its id) has loaded.
    // Deduped per article id for the session; the backend also dedups within a 2 minute window.
    useEffect(() => {
        if (!article?.articleId) return;

        const sessionKey = `article_view_recorded_${article.articleId}`;
        if (sessionStorage.getItem(sessionKey)) return;
        sessionStorage.setItem(sessionKey, 'true');

        recordArticleView(
            pagesUserId,
            article.articleId,
            clientId,
            msalInfo,
            requiresAuthentication
        );
    }, [
        article?.articleId,
        pagesUserId,
        clientId,
        msalInfo,
        requiresAuthentication
    ]);

    // Position the tags sidebar to align with the article card
    // Both vertical and horizontal positioning relative to article card (50px gap from card border)
    useEffect(() => {
        if (!article?.tags?.length) return;

        const SIDEBAR_GAP = 50; // Gap between sidebar and article card border
        const SIDEBAR_WIDTH = 280;

        const positionTagsSidebar = () => {
            const tagsSidebar = tagsSidebarRef.current;
            const articleCard = document.querySelector(
                '.answer-card'
            ) as HTMLElement;

            if (tagsSidebar && articleCard) {
                const articleCardRect = articleCard.getBoundingClientRect();
                const articleCardTop = articleCardRect.top;
                const articleCardRight = articleCardRect.right;

                // Set vertical position aligned with article card top
                tagsSidebar.style.top =
                    articleCardTop < 0 ? '1px' : `${articleCardTop}px`;

                // Position 50px to the right of the article card border
                const rightPosition =
                    window.innerWidth -
                    articleCardRight -
                    SIDEBAR_GAP -
                    SIDEBAR_WIDTH;
                tagsSidebar.style.right = `${Math.max(rightPosition, 20)}px`;

                tagsSidebar.classList.add('aligned');
            }
        };

        // Position after content loads
        const timeoutId = setTimeout(positionTagsSidebar, 100);

        // Reposition on scroll
        const handleScroll = () => {
            positionTagsSidebar();
        };

        // Reposition on resize
        window.addEventListener('resize', positionTagsSidebar);
        window.addEventListener('scroll', handleScroll);

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', positionTagsSidebar);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [article?.tags]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            const dropdown = tagsDropdownRef.current;

            if (isDropdownOpen && dropdown && !dropdown.contains(target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDropdownOpen]);

    // Close dropdown on resize to desktop (where sidebar is always visible)
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 1440) {
                setIsDropdownOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    if (isLoading) {
        return <LoadingSpinner />;
    }

    // A 404 (article route) surfaces as an ApiError; a 204 No Content resolves to a null
    // article. Both mean the slug has no published article, so show the not-found page.
    if (error instanceof ApiError && error.status === 404) {
        return <NotFoundPage message='Article not found.' />;
    }

    if (error) {
        return <DisplayQueryError error={error} refetch={refetch} />;
    }

    if (!article) {
        return <NotFoundPage message='Article not found.' />;
    }

    const hasTags = article?.tags && article.tags.length > 0;

    return (
        <>
            <DocumentHead
                title={' - ' + article?.title}
                description={article?.title}
                hideServerHeaders={structuredDataEnabled}
            />
            <Header headerClassName='mini' />
            <section className='white-bg py-5'>
                <Container>
                    <Row>
                        <Col xs={12}>
                            <Card className='custom-card answer-card article-card full-article mt-3'>
                                <CardBody className='custom-card-body'>
                                    <div className='d-flex justify-content-between align-items-center'>
                                        <h1 className='pr-3 mb-0'>
                                            {article?.title}
                                        </h1>
                                        {/* Hamburger dropdown for medium screens - positioned next to title */}
                                        {hasTags && (
                                            <div
                                                className='tags-dropdown-wrapper'
                                                ref={tagsDropdownRef}
                                            >
                                                <button
                                                    className='tags-hamburger-btn'
                                                    onClick={toggleDropdown}
                                                    aria-label='Toggle related topics'
                                                    aria-expanded={
                                                        isDropdownOpen
                                                    }
                                                >
                                                    <i className='fa-solid fa-tags'></i>
                                                </button>
                                                {isDropdownOpen && (
                                                    <div className='tags-dropdown-menu'>
                                                        <div className='tags-list'>
                                                            {article?.tags?.map(
                                                                (tag) => (
                                                                    <Link
                                                                        key={
                                                                            tag
                                                                        }
                                                                        className='btn btn-outline-primary btn-pill m-1 px-2 py-1'
                                                                        to={`/topic/${encodeURIComponent(
                                                                            tag
                                                                        )}`}
                                                                        onClick={() =>
                                                                            setIsDropdownOpen(
                                                                                false
                                                                            )
                                                                        }
                                                                    >
                                                                        <small>
                                                                            {
                                                                                tag
                                                                            }
                                                                        </small>
                                                                    </Link>
                                                                )
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <hr />
                                    <CardText className='mt-3'>
                                        <HtmlComponent
                                            html={article?.content}
                                            isMarkdown={article?.isMarkdown}
                                            isArticle={true}
                                        />
                                    </CardText>
                                </CardBody>
                                <hr />
                                {/* Mobile/tablet tags - shown inline at bottom */}
                                <Row className='article-tags-inline'>
                                    <Col xs={12}>
                                        <div className='align-self-start'>
                                            {article?.tags?.map((tag) => (
                                                <Link
                                                    key={tag}
                                                    className='btn btn-outline-primary btn-pill m-1 px-2 py-1'
                                                    to={`/topic/${encodeURIComponent(
                                                        tag
                                                    )}`}
                                                >
                                                    <small>{tag}</small>
                                                </Link>
                                            ))}
                                        </div>
                                    </Col>
                                </Row>
                                {article && (
                                    <div className='d-flex flex-column justify-content-center align-items-center mt-3'>
                                        <h5>Helpful?</h5>
                                        <div>
                                            <FeedbackBtn
                                                sessionKey={`${article?.articleId}-interaction-good`}
                                                good
                                                onClick={() =>
                                                    onGiveArticleFeedback(
                                                        `${article?.articleId}-interaction-good`,
                                                        pagesUserId,
                                                        article?.articleId,
                                                        ArticleMetricType.FeedbackGood,
                                                        clientId,
                                                        msalInfo,
                                                        requiresAuthentication
                                                    )
                                                }
                                            />
                                            <FeedbackBtn
                                                sessionKey={`${article.articleId}-interaction-bad`}
                                                onClick={() =>
                                                    onGiveArticleFeedback(
                                                        `${article.articleId}-interaction-bad`,
                                                        pagesUserId,
                                                        article.articleId,
                                                        ArticleMetricType.FeedbackBad,
                                                        clientId,
                                                        msalInfo,
                                                        requiresAuthentication
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                )}
                            </Card>
                        </Col>
                    </Row>
                </Container>
                {/* Desktop sticky tags sidebar */}
                {hasTags && (
                    <aside
                        ref={tagsSidebarRef}
                        className='article-tags-sidebar'
                    >
                        <h3>Related Topics</h3>
                        <div className='tags-list'>
                            {article?.tags?.map((tag) => (
                                <Link
                                    key={tag}
                                    className='btn btn-outline-primary btn-pill m-1 px-2 py-1'
                                    to={`/topic/${encodeURIComponent(tag)}`}
                                >
                                    <small>{tag}</small>
                                </Link>
                            ))}
                        </div>
                    </aside>
                )}
            </section>
        </>
    );
};

export default Article;
